/**
 * Request-scoped Auth0 contract for the shareable /signup walkthrough.
 *
 * The ordinary login flow deliberately does not use these options. Guided
 * signup asks Universal Login to open in signup mode and request a fresh
 * account choice, then carries its activation destination in Auth0 appState.
 * The same builder is used by the first CTA, callback retries, and account
 * switching on the activation page so those entry points cannot drift.
 */
import type { AppState, RedirectLoginOptions } from '@auth0/auth0-react';

export const GUIDED_SIGNUP_RETURN_TO = '/pending-approval?from=signup';

const GUIDED_SIGNUP_FLOW = 'guided-signup';

interface GuidedSignupAppState extends AppState {
  flow: typeof GUIDED_SIGNUP_FLOW;
  returnTo: typeof GUIDED_SIGNUP_RETURN_TO;
}

export function buildGuidedSignupLoginOptions(): RedirectLoginOptions<GuidedSignupAppState> {
  return {
    appState: {
      flow: GUIDED_SIGNUP_FLOW,
      returnTo: GUIDED_SIGNUP_RETURN_TO,
    },
    authorizationParams: {
      screen_hint: 'signup',
      prompt: 'select_account',
      max_age: 0,
    },
  };
}

function isGuidedSignupAppState(value: unknown): boolean {
  if (value === null || typeof value !== 'object') return false;

  const appState = value as Partial<GuidedSignupAppState>;
  return (
    appState.flow === GUIDED_SIGNUP_FLOW &&
    appState.returnTo === GUIDED_SIGNUP_RETURN_TO
  );
}

/** Auth0 preserves transaction appState on callback AuthenticationError. */
export function isGuidedSignupCallbackError(error?: Error): boolean {
  if (!error || !('appState' in error)) return false;
  return isGuidedSignupAppState(error.appState);
}

export function isGuidedSignupActivationSearch(search: string): boolean {
  return new URLSearchParams(search).get('from') === 'signup';
}
